import { Component, ViewEncapsulation, ViewChildren, ElementRef, QueryList } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { GridActionFocusRestore } from '../../grid-action-focus-restore';

@Component({
  selector: 'action-cell',
  templateUrl: './actions-renderer.component.html',
  encapsulation: ViewEncapsulation.None
})

export class ActionsRendererComponent implements ICellRendererAngularComp {
  @ViewChildren('actionLink', { read: ElementRef }) actionLinks: QueryList<ElementRef>;
  actionButtons: any[] = [];
  params: ICellRendererParams;
  buttonAlignmentClass = 'text-right';
  activeActionIndex: number = -1;

  constructor() {}

  onUserAction(action) {
    this.params.data.userAction = action;
    this.params.node.setData(this.params.data);
    this.params.eGridCell.click();
  }

  private getEnabledActionIndices(): number[] {
    return this.actionButtons
      .map((btn, index) => ({ btn, index }))
      .filter(({ btn }) => !btn.isDisabled && !btn.isHidden)
      .map(({ index }) => index);
  }

  onActionKeyDown(event: KeyboardEvent, actionIndex: number) {
    const key = event.key;

    // Handle Tab to cycle to next enabled action icon
    if (key === 'Tab') {
      const enabledIndices = this.getEnabledActionIndices();
      const currentPosition = enabledIndices.indexOf(actionIndex);

      if (event.shiftKey) {
        // Shift+Tab: move to previous enabled action
        if (currentPosition > 0) {
          event.preventDefault();
          event.stopPropagation();
          const prevIndex = enabledIndices[currentPosition - 1];
          this.focusActionButton(prevIndex);
          this.activeActionIndex = prevIndex;
          return;
        }
      } else {
        // Tab: move to next enabled action
        if (currentPosition < enabledIndices.length - 1) {
          event.preventDefault();
          event.stopPropagation();
          const nextIndex = enabledIndices[currentPosition + 1];
          this.focusActionButton(nextIndex);
          this.activeActionIndex = nextIndex;
          return;
        }
      }
      // If no more actions to cycle, let Tab propagate naturally
      this.activeActionIndex = -1;
      return;
    }

    // Handle Enter/Space to trigger action
    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      const button = this.actionButtons[actionIndex];
      if (button && !button.isDisabled) {
        this.rememberKeyboardActionFocus(event.currentTarget as HTMLElement, actionIndex);
        this.onUserAction(button.userAction);
      }
      return;
    }
  }

  private rememberKeyboardActionFocus(anchor: HTMLElement | null, actionIndex: number): void {
    const rowIndex = this.params?.node?.rowIndex;
    const colId = this.params?.column?.getColId?.();

    if (rowIndex == null || !colId) {
      return;
    }

    const gridRoot = (anchor?.closest('.ag-theme-alpine') as HTMLElement | null)
      || (anchor?.closest('.ag-root-wrapper') as HTMLElement | null);
    const gridRoots = Array.from(document.querySelectorAll('.ag-theme-alpine')) as HTMLElement[];
    const gridDomIndex = gridRoot ? gridRoots.indexOf(gridRoot) : -1;
    const gridFocusKey = gridRoot?.getAttribute('data-grid-focus-key') || undefined;
    const rowData = this.params?.data || {};
    const identityKeys = ['id', 'uuid', '_id', 'project_id', 'projectId', 'service_product_id', 'serviceProductId', 'roadmapId', 'machine_id', 'machineId', 'userId', 'email', 'name'];
    const rowIdentity = identityKeys.reduce((identity, key) => {
      if (rowData[key] !== undefined && rowData[key] !== null && rowData[key] !== '') {
        identity[key] = rowData[key];
      }
      return identity;
    }, {} as Record<string, unknown>);

    GridActionFocusRestore.set({
      actionIndex,
      colId,
      expiresAt: Date.now() + 180000,
      gridFocusKey,
      gridDomIndex,
      rowIdentity,
      rowIndex,
    });
  }

  private focusActionButton(index: number) {
    if (index >= 0 && this.actionLinks && this.actionLinks.length > index) {
      const element = this.actionLinks.toArray()[index]?.nativeElement as HTMLElement;
      if (element) {
        element.focus();
      }
    }
  }

  initilize(params: ICellRendererParams) {
    if (params && params.data) {
      this.params = params;
      this.actionButtons = JSON.parse(JSON.stringify(params.colDef.headerComponentParams));
      for (let i = 0; i < this.actionButtons.length; i++) {
        this.actionButtons[i].isDisabled = this.actionButtons[i].disableProperty && this.params.data[this.actionButtons[i].disableProperty];
        this.actionButtons[i].isLoading = this.actionButtons[i].loadingProperty && this.params.data[this.actionButtons[i].loadingProperty];
        this.actionButtons[i].isDisabled = this.actionButtons[i].isDisabled || this.actionButtons[i].isLoading;
        this.actionButtons[i].resolvedDisabledTooltip = this.actionButtons[i].disabledTooltipProperty
          ? this.params.data[this.actionButtons[i].disabledTooltipProperty]
          : this.actionButtons[i].disabledTooltip;
        this.actionButtons[i].isGreyedOut = this.actionButtons[i].greyOutProperty && this.params.data[this.actionButtons[i].greyOutProperty];
        this.actionButtons[i].isHidden = this.actionButtons[i].hideProperty && this.params.data[this.actionButtons[i].hideProperty];
        this.buttonAlignmentClass = this.actionButtons[i].buttonAlignmentClass ?? this.buttonAlignmentClass;
      }
    }
    else {
      this.actionButtons = [];
    }
    this.activeActionIndex = -1;
  }

  // called on init
  agInit(params: any): void {
    this.initilize(params);
  }

  // called when the cell is refreshed
  refresh(params: any): boolean {
    this.initilize(params);
    return true;
  }

}
